/* DIE PLANUNGSKARTE ZUM ANSEHEN (.455).
   ORIGINALTEXT MARVIN (22.10.2026)
     „Der Tiefbauaufwand, den wir einzeichnen in den Plan, von der Schätzung
      her, diese Karte soll zu öffnen sein in dieser Kachel, sowohl für uns
      als auch für die Partner, dass die sehen, was wir da so eingezeichnet
      haben und mit der Oberfläche als Unterstützung. Nicht nur die reinen
      Werte, sondern auch die karte."
   Dieselbe Karte wie in der Planung (Kartenflaeche), nur ohne Werkzeuge und
   ohne Klick. Gezeigt werden Graben je Oberfläche, Leerrohr gestrichelt,
   Gruben und Meter je Oberfläche. Stunden und Tage kommen gar nicht erst vom
   Server: sie sind unsere Kalkulation. */
import { Fragment, useEffect, useState } from 'react';
import { api } from '../api.ts';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';
import Kartenflaeche from './Kartenflaeche.tsx';
import { grubenRadius, type Kartenlage } from './Tiefbaukarte.tsx';
import type { Kartenschalter } from './kartenstart.ts';

const START_ANSICHT: Kartenschalter = { luftbild: true, schrift: true, grenzen: false, nummern: false };

const zahl = (n: number) => (Math.round(n * 10) / 10).toLocaleString('de-DE');

export default function Planungskarte({ auftragId, untertitel, zu }: { auftragId: string; untertitel?: string; zu: () => void }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [lage, setLage] = useState<Kartenlage | null>(null);

  useEffect(() => {
    api.planungskarte(auftragId).then((r: any) => {
      setD(r);
      if (r.mitte) setLage({ mitte: r.mitte, zoom: r.zoom ?? r.startstufe });
    }).catch((e: any) => setFehler(ausFehler(e)));
  }, [auftragId]);

  const farbe = (k: string) => d?.oberflaechen?.find((o: any) => o.wert === k)?.farbe ?? '#8e97a4';
  const text = (k: string) => d?.oberflaechen?.find((o: any) => o.wert === k)?.text ?? 'nicht bestimmt';
  const flaechen = Object.entries((d?.flaechen ?? {}) as Record<string, number>).filter(([, m]) => m > 0);
  const genutzt = new Set<string>([...(d?.abschnitte ?? []).map((a: any) => a.klasse), ...(d?.gruben ?? []).map((g: any) => g.klasse)]);

  return (
    <Dialog titel="Geplanter Leitungsweg" untertitel={untertitel ?? d?.adresse} breite={980} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {!d && !fehler && <p className="leise">Wird geladen …</p>}
      {d && !lage && <p className="leise">Für diesen Auftrag fehlt ein Kartenpunkt.</p>}
      {d && lage && (
        <>
          <Kartenflaeche
            auftragId={auftragId}
            lage={lage} setzeLage={setLage}
            start={START_ANSICHT}
            daten={d}
            wunschBreite={940} wunschHoehe={560}
            rechts={<span className="klein leise">{d.abschnitte.length} {d.abschnitte.length === 1 ? 'Abschnitt' : 'Abschnitte'}, {d.gruben_zahl} {d.gruben_zahl === 1 ? 'Grube' : 'Gruben'}</span>}
            kinder={(nach, mpp) => (
              <>
                {d.abschnitte.map((a: any, i: number) => {
                  const p = nach(a.a), q = nach(a.b);
                  const m = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
                  return (
                    <g key={i}>
                      <line x1={p.x} y1={p.y} x2={q.x} y2={q.y} stroke={farbe(a.klasse)} strokeWidth={13}
                            strokeLinecap="round" strokeDasharray={a.leerrohr ? '5 10' : undefined} />
                      <line x1={p.x} y1={p.y} x2={q.x} y2={q.y} stroke="#fff" strokeWidth={3}
                            strokeDasharray={a.leerrohr ? '5 10' : undefined} />
                      <circle cx={m.x} cy={m.y} r={11} fill="#1f3864" stroke="#fff" strokeWidth={2} />
                      <text x={m.x} y={m.y + 4} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={700}>{i + 1}</text>
                    </g>
                  );
                })}
                {d.gruben.map((g: any, i: number) => {
                  if (!g?.punkt) return null;
                  const q = nach(g.punkt);
                  const r = grubenRadius(mpp);
                  return (
                    <g key={'g' + i}>
                      <rect x={q.x - r} y={q.y - r} width={r * 2} height={r * 2} rx={Math.max(2, r * 0.38)}
                            fill={farbe(g.klasse)} stroke="#e9954a" strokeWidth={Math.max(2, Math.min(4, r * 0.3))} />
                      <text x={q.x} y={q.y + 4} textAnchor="middle" fill="#fff" fontSize={11} fontWeight={700}>G{i + 1}</text>
                    </g>
                  );
                })}
              </>
            )}
          />
          <div className="legende">
            {d.oberflaechen.filter((o: any) => genutzt.has(o.wert)).map((o: any) => (
              <span key={o.wert}><i style={{ background: o.farbe }} />{o.text}</span>
            ))}
            {d.abschnitte.some((a: any) => a.leerrohr) && <span><i className="gestrichelt" />Leerrohr liegt</span>}
          </div>
          <dl className="felder">
            <dt>Trasse gesamt</dt><dd>{zahl(d.meter)} m</dd>
            {flaechen.map(([k, m]) => (<Fragment key={k}><dt>{text(k)}</dt><dd>{zahl(m)} m</dd></Fragment>))}
            <dt>Montagegruben</dt><dd>{d.gruben_zahl}</dd>
          </dl>
          <p className="klein leise">Die Linien sind von Hand eingezeichnet und zeigen den geplanten Weg. Vor Ort gilt, was die Leitungsauskunft und der Eigentümer sagen.</p>
        </>
      )}
    </Dialog>
  );
}
